// ---------------------------------------------------------------------------
// Tenant Admin module — Tenant Members list page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api, getDebugHeaders } from '../lib/api';
const apiBase = import.meta.env.VITE_API_BASE_URL || '';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { SearchInput } from '../components/SearchInput';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Invite member form
// ---------------------------------------------------------------------------

function InviteMemberForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation('admin');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('tenant_viewer');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roleOptions = useMemo(
    () => [
      { value: 'tenant_admin', label: t('members.roles.tenant_admin') },
      { value: 'tenant_analyst', label: t('members.roles.tenant_analyst') },
      { value: 'tenant_viewer', label: t('members.roles.tenant_viewer') },
    ],
    [t],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const idempotencyKey = `invite_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await fetch(`${apiBase}/v1/admin/tenants/${tenantId}/members/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...getDebugHeaders(),
        },
        body: JSON.stringify({ email, role }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      setEmail('');
      setRole('tenant_viewer');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('members.errors.inviteFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface-raised rounded-sm shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-ink mb-4">{t('members.form.title')}</h3>

      {error && (
        <div className="mb-4 p-3 bg-danger-tint border border-danger/30 rounded text-danger text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="member-email" className="block text-sm font-medium text-ink mb-1">
            {t('members.form.email')}
          </label>
          <input
            id="member-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('members.form.emailPlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="member-role" className="block text-sm font-medium text-ink mb-1">
            {t('members.form.role')}
          </label>
          <select
            id="member-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-raised"
          >
            {roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? t('members.actions.sending') : t('members.actions.send')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function MembersPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const columns = useMemo<Column<admin.TenantMemberSummary>[]>(
    () => [
      {
        key: 'displayName',
        header: t('members.columns.name'),
        render: (m) => (
          <span className="text-sm font-medium text-ink">
            {m.displayName ?? '--'}
          </span>
        ),
      },
      {
        key: 'email',
        header: t('members.columns.email'),
        render: (m) => (
          <span className="text-sm text-ink-muted">{m.email}</span>
        ),
      },
      {
        key: 'status',
        header: t('members.columns.status'),
        render: (m) => <StatusBadge value={m.status} />,
      },
      {
        key: 'createdAt',
        header: t('members.columns.created'),
        render: (m) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(m.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t],
  );
  const { tenantId } = useProjectContext();
  const [members, setMembers] = useState<admin.TenantMemberSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadMembers = useCallback(() => {
    if (!tenantId) {
      setMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    api.admin.listTenantMembers(tenantId)
      .then((res) => {
        setMembers(res.data);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : t('members.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [tenantId, t]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // useMemo MUST be called before any conditional return (Rules of Hooks)
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      (m) =>
        m.email.toLowerCase().includes(q) ||
        (m.displayName ?? '').toLowerCase().includes(q),
    );
  }, [members, searchQuery]);

  if (!tenantId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('members.headings.list')}</h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">
            {t('members.messages.selectProject')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadMembers} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('members.headings.list')}</h1>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
        >
          {showCreate ? t('members.actions.cancel') : t('members.actions.invite')}
        </button>
      </div>

      {showCreate && (
        <InviteMemberForm
          tenantId={tenantId}
          onCreated={() => {
            setShowCreate(false);
            loadMembers();
          }}
        />
      )}

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('members.filters.searchPlaceholder')}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredMembers}
        rowKey={(m) => m.id}
        emptyMessage={t('members.empty')}
        ariaLabel={t('members.ariaLabel')}
      />
    </div>
  );
}
